/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import _, { toNumber } from 'lodash';
import { addClass, DataType, toggleClass } from '@grapecity/wijmo';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import {
  AsstRvwType,
  AsstTpCd,
  IdleAssetDetail,
  openEmsSPLink,
  RvwStatCd,
} from 'models/asst/IdleAsset';
import { getExcelFileName } from 'utils/ExcelUtil';
import { findIdleAssetEquipments } from 'apis/asst/IdleAsset';
import { useMessageBar } from 'hooks/useMessageBar';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import {
  findIdleAssetReviewResult,
  saveIdleAssetReviewResult,
  saveIdleAssetReviewResultEqp,
} from 'apis/asst/IdleAssetReviewResult';
import { useCommonModal } from 'hooks/useCommonModal';
import { IdleAssetReviewResult } from 'models/asst/IdleAssetReviewResult';
import useEvent from 'react-use-event-hook';
import { ComboBox } from '../../../components/selects/ComboBox';
import { CrudCode } from '../../../models/common/Edit';
import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import CustomInputWithSearch from '../../../components/inputs/CustomInputWithSearch';
import { useLoading } from '../../../components/process/Loading';
import useSessionStore from '../../../stores/useSessionStore';
import GridSettingButtons from '../../../components/grids/GridSettingButtons';

interface Prop {
  onClikDetail: (item: IdleAssetDetail) => void;
  openMode?: AsstRvwType; //'REQUEST' | 'REPLY' | 'READ';요청, 회신, 결과(읽기전용)
  asstTpCd?: string; // 자산 분류 : EQP 설비 , ETC 기타자산, SP Spare Parts
  gridHeight?: number; //그리드 높이
  onRefresh?: () => void;
  condition?: any;
}

/**
 * 불용자산 설비/기타자산/Spare Parts 그리드
 * @param onClikDetail
 * @param openMode
 * @param asstTpCd
 * @param gridHeight
 * @param onRefresh
 * @param condition
 * @param ref
 * @constructor
 */
const IdleAssetEquipmentGrid = (
  { onClikDetail, openMode, asstTpCd, gridHeight, onRefresh, condition }: Prop,
  ref
) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [rowData, setRowData] = useState<IdleAssetReviewResult[]>([]);
  const [subTitle, setSubTitle] = useState<string>('');
  const { openMessageBar } = useMessageBar();

  const [code, setCode] = useState<any>();
  const { openCommonModal } = useCommonModal();
  const [asstRvwRltDivsCd, setAsstRvwRltDivsCd] = useState<Code[]>([]);
  const { openLoading } = useLoading();
  const userSession = useSessionStore();
  //일괄적용
  const [getGatingMgrTpSelected, setGatingMgrTpSelected] = useState<string>('');
  const [getRvwRltCtn, setRvwRltCtn] = useState<string>('');
  const [checkedItems, setCheckedItems] = useState<IdleAssetReviewResult[]>();
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [getCurrCdPre, setCurrCdPre] = useState<string>('');
  const [activeAsstTpCd, setAsstTpCd] = useState<string>('');
  const [activeGridId, setActiveGridId] = useState<string>('');
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
    const randomNum = Math.random() * 1000;
    setActiveGridId('iaeqgDummy' + randomNum);
  }, []);

  useImperativeHandle(ref, () => ({
    search: (params) => {
      setActiveGridId('IidleAsst' + params.asstTpCd + 'Grid');
      setAsstTpCd(params.asstTpCd);
      handleSearch(params);
    },
  }));

  const getCommonCodes = async () => {
    const rvwRltDivsCd: Code[] = await getCommonCodeNames('RVW_RLT_DIVS_CD'); // 검토결과
    const asstRvwTpCd: Code[] = await getCommonCodeNames('ASST_RVW_TP_CD'); // 검토유형
    const currCd: Code[] = await getCommonCodeNames('CURR_CD'); // 통화단위

    const asstRvwRltDivsCd2: Code[] = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'ASST_RVW_RLT_DIVS_CD',
    });
    // 검토유형2
    const rvwStatCd: Code[] = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'RVW_STAT_CD',
    }); // 검토상태
    setAsstRvwRltDivsCd(asstRvwRltDivsCd2);
    setCode({
      rvwRltDivsCd: rvwRltDivsCd,
      rvwStatCd: rvwStatCd,
      asstRvwTpCd: asstRvwTpCd,
      currCd: currCd,
    });
  };

  //통화단위 변경시 금액변경 (조회용)
  const setCurrChangeView = async (value) => {
    if (_.isEmpty(rowData[0].currCd)) return;
    const tgtCurr = value == '' ? getCurrCdPre : value;

    //환율찾기
    const currCdTgt = code.currCd.filter((o) => o.cmnCd === tgtCurr);
    const currCdSrc = code.currCd.filter((o) => o.cmnCd === rowData[0].currCd);

    //값변경..
    setRowData((prev) =>
      prev.map((row) => {
        const deprAmt = parseFloat(row.deprAmt ?? '0') || 0;
        const asstAcqrAmt = parseFloat(row.asstAcqrAmt ?? '0') || 0;
        const numFrom = parseFloat(currCdSrc[0].optValCtn2.replace(/,/g, ''));
        const numTo = parseFloat(currCdTgt[0].optValCtn2.replace(/,/g, ''));
        const deprAmtCal = (deprAmt * numFrom) / numTo;
        const asstAcqrAmtCal = (asstAcqrAmt * numFrom) / numTo;
        return {
          ...row,
          currCd: tgtCurr,
          deprAmt: deprAmtCal.toFixed(2),
          asstAcqrAmt: asstAcqrAmtCal.toFixed(2),
        };
      })
    );
  };

  /**
   * 설비목록조회
   * openMode별로 다르게 조회
   * @param params
   */
  const handleSearch = (params) => {
    if (openMode === AsstRvwType.REPLY) {
      if (_.isEmpty(params?.tgtCopCd)) return; //검토결과 회신화면에서는 법인이 없는 경우 조회 못하도록 함.
      findIdleAssetReviewResult(params).then((result) => {
        if (_.isNull(result) || result.length === 0) return;
        result.forEach((item) => {
          item.asstQtyInvOn = item.asstQtyInv; // 원래 재고 복사
          item.asstQtyInv = (
            toNumber(item.asstQtyInv ?? 0) - toNumber(item.nedQty ?? 0)
          ).toString();
        });
        //원 통화단위  저장
        setCurrCdPre(result[0]?.currCd || '');
        setRowData(result); //
      });
    } else {
      //검토결과 readonly
      findIdleAssetEquipments(params.rvwReqId).then((result) => {
        if (_.isNull(result) || result.length === 0) return;
        result.forEach((item) => {
          item.asstQtyInv = item.asstQty;
        });
        //원 통화단위  저장
        setCurrCdPre(result[0]?.currCd || '');
        setRowData(result);
        onClikDetail(result[0]);
      });
    }
  };

  //엑셀다운로드
  const handleExportExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });

    const sheetName =
      condition?.asstTpCd === AsstTpCd.SPARE_PARTS
        ? t('asst.label.요청 SP 상세 내용', '요청 SP 상세 내용')
        : condition?.asstTpCd === AsstTpCd.EQUIPMENT
        ? t('asst.label.요청 설비 리스트', '요청 설비 리스트')
        : t('asst.label.요청 기타자산 리스트', '요청 기타자산 리스트');

    book.sheets[0].name = sheetName;
    book.saveAsync(getExcelFileName('[' + condition?.rvwReqId + '] ' + sheetName));
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;
      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;
      const item = grid.rows[ht.row].dataItem;
      const col = grid.columns[ht.col].binding;
      switch (col) {
        // EMS 링크
        case 'splink':
          if (e.target instanceof HTMLSpanElement) {
            openEmsSPLink(item.asstInvNo); //
          }
          break;
      }

      onClikDetail(item);
    });

    gridRef.current.refresh();
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    const binding = panel.columns[col].binding;
    const item = panel.rows[row].dataItem;

    //header
    if (CellType.ColumnHeader === panel.cellType) {
      // 필수항목 : 검토결과, 검토결과내용, sp 요청수량
      if (openMode === AsstRvwType.REPLY) {
        if (
          ['rvwRltDivsCd', 'rvwRltCtn'].includes(binding) ||
          (activeAsstTpCd === AsstTpCd.SPARE_PARTS && ['nedQty'].includes(binding))
        ) {
          addClass(cell, 'dot');
        }
      }
    }

    //cell
    if (CellType.Cell === panel.cellType) {
      const readonly = item.asstRvwTpCd !== 'RLT_INS' || openMode === AsstRvwType.READ;

      //검토결과 아이콘
      if ('rvwRltDivsCd' === binding) {
        if (openMode === AsstRvwType.READ) {
          let html = `<span></span>`;
          if ('PSBL' === item.rvwRltDivsCd) {
            html = `<span class="green">${t('asst.label.활용가능', '활용가능')}</span>`;
          } else if ('BOOK' === item.rvwRltDivsCd) {
            html = `<span class="yellow">${t('asst.label.예약중', '예약중')}</span>`;
          } else if (!_.isEmpty(item.rvwRltDivsCd)) {
            html = `<span class="red">${t('asst.label.활용불가', '활용불가')}</span>`;
          }
          cell.innerHTML = html;
        }
      }
    }
  });

  const beginningEdit = useEvent((s, e) => {
    const col = s.columns[e.col];

    if (col.binding === 'nedQty') {
      const item = s.rows[e.row].dataItem;
      item._orgNedQty = item.nedQty;
      const cell = s.cells.getCellElement(e.row, e.col);
      // editor(input) 생성될 때까지 잠깐 기다림
      setTimeout(() => {
        // 셀 안쪽에서 input 요소 찾기
        const input = cell.querySelector('input');
        if (input) {
          // 숫자만 입력되도록 필터링
          input.addEventListener('input', () => {
            input.value = input.value.replace(/[^0-9]/g, '');
          });
        }
      });
    }
  });

  const onCellEditEnded = (grid, e) => {
    // 바운딩된 컬럼명
    const col = grid.columns[e.col].binding;
    const item = grid.collectionView.currentItem;

    if (col === 'rvwRltDivsCd') {
      // PSBL = 활용가능 / IMPB = 활용불가
      if (item.rvwRltDivsCd === 'IMPB') {
        // 활용불가 선택 → 요청수량 0으로 강제
        item.nedQty = '0';
        // 잔여수량 = 재고수량
        item.asstQtyInv = item.asstQtyInvOn;
      }
      grid.collectionView.commitEdit();
      return;
    }

    if (col === 'nedQty') {
      const prev = item._orgNedQty; // 편집 전 값
      const curr = item.nedQty; // 편집 후 값
      if (prev !== undefined && prev === curr) return;
      const stock = Number(item.asstQtyInvOn);
      const req = Number(curr);

      if (_.isEmpty(item.nedQty)) return;
      // 1) 숫자 아님 or 음수
      if (isNaN(req) || req <= 0) {
        item.nedQty = '0';
        item.asstQtyInv = item.asstQtyInvOn;
        item.rvwRltDivsCd = 'IMPB'; //활용불가로 변경
      } else {
        // 2) 정상 입력
        // 재고보다 크면 잘못 입력으로 간주 → 전체 초기화
        if (req > stock) {
          item.nedQty = '0';
          item.asstQtyInv = item.asstQtyInvOn;
          item.rvwRltDivsCd = 'IMPB'; //활용불가로 변경
          openMessageBar({
            type: 'error',
            content: t(
              'com.label.요청수량이 재고수량보다 많습니다.',
              '요청수량이 재고수량보다 많습니다.'
            ),
          });
        } else {
          item.asstQtyInv = (stock - req).toString();
          item.rvwRltDivsCd = 'PSBL'; //활용가능으로 변경
        }
      }
      grid.collectionView.commitEdit();
    }
  };

  /**
   * layout
   * 설비ID, 설비명, 자산번호, 자산명, 자산취득일자, 취득가액, 잔존가액, 통화단위, 상태, 검토결과, 검토내용, 검토일자, 코스트센터, 코스트센터명, 비고 (설비)
   *               자산번호, 자산명, 자산취득일자, 취득가액, 잔존가액, 통화단위, 상태, 검토결과, 검토내용, 검토일자, 코스트센터, 코스트센터명, 비고 (기타자산)
   * Spare parts
   * 품목코드, 품목명, 재고유형, EMS창고명, 재고단가, 수량, 요청수량, 재고비용, 통화단위, 품목상태, 검토결과, 검토내용, S/P 실물, 단위, 재고처리번호, 대체품목, 검토일자
   */
  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        width: 40,
        isReadOnly: true,
        visible: false,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'asstTpCdNm',
        header: String(t('asst.label.분류', '분류')),
        align: 'center',
        width: 80,
        isReadOnly: true,
        visible: false,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('asst.label.공정', '공정')),
        align: 'center',
        width: 100,
        visible: false,
        isReadOnly: true,
      },
      {
        binding: 'eqpId',
        header: String(t('asst.label.설비ID', '설비ID')),
        align: 'center',
        width: 100,
        isReadOnly: true,
        visible: activeAsstTpCd === AsstTpCd.EQUIPMENT,
      },
      {
        binding: 'eqpNm',
        header: String(t('asst.label.설비명', '설비명')),
        align: 'left',
        visible: activeAsstTpCd === AsstTpCd.EQUIPMENT,
        width: 150,
        isReadOnly: true,
      },
      {
        binding: 'asstNo',
        header: String(t('asst.label.자산번호', '자산번호')),
        align: 'center',
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'asstNm',
        header: String(t('asst.label.자산명', '자산명')),
        align: 'left',
        width: 150,
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
      },
      // spare parts
      {
        binding: 'asstInvNo',
        header: String(t('asst.label.품목코드', '품목코드')),
        align: 'left',
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'asstInvNm',
        header: String(t('asst.label.품목명', '품목명')),
        align: 'left',
        width: 150,
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
      },
      {
        binding: 'invTpCd',
        header: String(t('asst.label.재고유형', '재고유형')),
        align: 'left',
        width: 100,
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
      },
      {
        binding: 'whNm',
        header: String(t('asst.label.EMS창고명', 'EMS창고명')),
        align: 'left',
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        width: 120,
        isReadOnly: true,
      },
      {
        binding: 'asstAcqrAmt',
        header: String(t('asst.label.재고단가', '재고단가')),
        align: 'right',
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        width: 100,
        isReadOnly: true,
        cellTemplate: (params) =>
          `${params.value ? Number(params.value || 0).toLocaleString() : ''}`,
      },
      {
        binding: 'asstQtyInv',
        header:
          openMode === AsstRvwType.REPLY
            ? String(t('asst.label.잔여수량', '잔여수량'))
            : String(t('asst.label.수량', '수량')),
        align: 'right',
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        width: 70,
        isReadOnly: true,
        cellTemplate: (params) =>
          `${params.value ? Number(params.value || 0).toLocaleString() : ''}`,
      },
      // 검토내역 수량 / 확정수량  , 결과회신 잔여수량 / 요청수량
      {
        binding: 'nedQty',
        header:
          openMode === AsstRvwType.REPLY
            ? String(t('asst.label.요청수량', '요청수량'))
            : String(t('asst.label.확정수량', '확정수량')),
        align: 'right',
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        width: 80,
        isReadOnly: openMode === AsstRvwType.READ,
        cellTemplate: (params) =>
          `${params.value ? Number(params.value || 0).toLocaleString() : ''}`,
      },
      {
        binding: 'amount',
        header: String(t('asst.label.재고비용', '재고비용')),
        align: 'right',
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        width: 100,
        isReadOnly: true,
        cellTemplate: (params) => {
          // 재고 단가 * 수량
          const item = params.item;
          const amountSum = item.asstAcqrAmt * item.asstQty;
          return amountSum.toLocaleString();
        },
      },
      {
        binding: 'currCd',
        header: String(t('asst.label.통화단위', '통화단위')),
        align: 'center',
        width: 80,
        visible: true,
        isReadOnly: true,
      },
      // {
      //   binding: 'rvwStatCdNm',
      //   header: String(t('asst.label.품목상태', '품목상태')),
      //   align: 'center',
      //   width: 80,
      //   isReadOnly: true,
      //   visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
      // },

      {
        binding: 'acqrDt',
        header: String(t('asst.label.자산취득일', '자산취득일')),
        align: 'center',
        width: 90,
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
      },
      {
        binding: 'asstAcqrAmt',
        header: String(t('asst.label.취득가액', '취득가액')),
        align: 'right',
        width: 100,
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
        cellTemplate: (params) =>
          `${params.value ? Number(params.value || 0).toLocaleString() : ''}`,
      },
      {
        binding: 'deprAmt',
        header: String(t('asst.label.잔존가액', '잔존가액')),
        align: 'right',
        width: 100,
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
        cellTemplate: (params) =>
          `${params.value ? Number(params.value || 0).toLocaleString() : ''}`,
      },

      {
        binding: 'rvwStatCdNm',
        header:
          activeAsstTpCd === AsstTpCd.SPARE_PARTS
            ? String(t('asst.label.품목상태', '품목상태'))
            : String(t('asst.label.상태', '상태')),
        align: 'center',
        width: 80,
        isReadOnly: true,
        // visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
      },
      {
        binding: 'rvwRltDivsCd',
        header: String(t('asst.label.검토결과', '검토결과')),
        align: 'center',
        width: 100,
        cssClass: openMode === AsstRvwType.READ ? 'WijmoTag' : 'WijmoSelect',
        isReadOnly: openMode === AsstRvwType.READ,
        dataMap: new DataMap(code?.rvwRltDivsCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'rvwRltCtn',
        header: String(t('asst.label.검토내용', '검토내용')),
        align: 'left',
        minWidth: 100,
        width: '*',
        isReadOnly: openMode === AsstRvwType.READ,
      },

      {
        binding: 'splink',
        header: String(t('asst.label.S/P 실물', 'S/P 실물')),
        align: 'center',
        width: 80,
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          return `<span class="yellow" style="cursor: pointer">${t(
            'asst.label.링크',
            '링크'
          )}</span>`;
        },
      },
      {
        binding: 'asstQtyUnitNm',
        header: String(t('asst.label.단위', '단위')),
        align: 'right',
        width: 80,
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
      },
      {
        binding: 'asstInvNo',
        header: String(t('asst.labal.재고처리번호', '재고처리번호')),
        align: 'right',
        width: 80,
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
      },
      {
        binding: 'atntItemNm',
        header: String(t('asst.labal.대체품목', '대체품목')),
        align: 'right',
        width: 80,
        visible: activeAsstTpCd === AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
      },
      {
        binding: 'rvwDt',
        header: String(t('asst.label.검토일', '검토일')),
        align: 'center',
        width: 90,
        isReadOnly: true,
        visible: openMode === AsstRvwType.REPLY,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('asst.label.검토일', '검토일')),
        align: 'center',
        width: 90,
        isReadOnly: true,
        visible: openMode !== AsstRvwType.REPLY,
      },

      {
        binding: 'cctrCd',
        header: String(t('asst.label.코스트센터', '코스트센터')),
        align: 'center',
        width: 100,
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
        isReadOnly: true,
        // visible: !resultCfm,
      },
      {
        binding: 'cctrNm',
        header: String(t('asst.label.코스트센터명', '코스트센터명')),
        align: 'left',
        width: 150,
        isReadOnly: true,
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
        // visible: !resultCfm,
      },
      {
        binding: 'rmk',
        header: String(t('asst.label.비고', '비고')),
        align: 'center',
        width: 100,
        isReadOnly: true,
        visible: activeAsstTpCd !== AsstTpCd.SPARE_PARTS,
      },

      {
        binding: 'progressRate',
        header: String(t('asst.label.검토율', '검토율')),
        align: 'center',
        width: 150,
        isReadOnly: true,
        visible: false,
        cellTemplate: CellMaker.makeLink({
          text: '${item.progressRate}',
          click: (e, ctx) => {
            onClikDetail(ctx.item);
          },
        }),
      },
      {
        binding: 'asstRvwTpCd',
        header: String(t('asst.label.검토유형', '검토유형')),
        align: 'center',
        width: 150,
        cssClass: 'WijmoSelect',
        isReadOnly: true,
        dataMap: new DataMap(code?.asstRvwTpCd || [], 'cmnCd', 'cmnCdNm'),
        visible: false,
      },
    ];
  }, [code, openMode, activeAsstTpCd, isChangeColumn]);

  //일괄적용
  const onBtContAll = () => {
    if ((checkedItems || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('gtng.msg.일괄적용할 항목을 선택해주세요.', '일괄적용할 항목을 선택해주세요.'),
      });
      return;
    }

    (checkedItems || []).forEach((o) => {
      // if (o.asstRvwTpCd !== 'RLT_INS') return;
      o.rvwRltDivsCd = getGatingMgrTpSelected === '' ? o.rvwRltDivsCd : getGatingMgrTpSelected; //결과선택안했을때는 그대로
      o.rvwRltCtn = getRvwRltCtn; //내용
      o.nedQty = o.rvwRltDivsCd === 'IMPB' ? '0' : o.nedQty;
      o.crudKey = CrudCode.UPDATE;
    });
    gridRef.current?.collectionView?.refresh();
  };
  const lastCheckedItem = useRef(null);

  //검토완료전 체크
  const validateSaveCondition = (type) => {
    if (_.isNull(rowData) || rowData.length == 0) return;

    const validStatusU = rowData.filter((element) => element.crudKey == CrudCode.UPDATE);
    if (validStatusU.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return false;
    }
    if (rowData.some((item) => _.isEmpty(item.rvwRltDivsCd))) {
      openMessageBar({
        type: 'error',
        content: t('asst.msg.검토결과를 선택해주세요.', '검토결과를 선택해주세요.'),
      });
      return false;
    }
    if (rowData.some((item) => _.isEmpty(item.rvwRltCtn))) {
      openMessageBar({
        type: 'error',
        content: t('asst.msg.검토내용을 입력해주세요.', '검토내용을 입력해주세요.'),
      });
      return false;
    }

    if (
      activeAsstTpCd !== 'SP' &&
      rowData.some((item) => item.rvwRltDivsCd === 'PSBL' && item.rvwStatCd === 'RESV')
    ) {
      openMessageBar({
        type: 'error',
        content: t(
          'asst.msg.예약중인 설비는 활용가능으로 선택할수없습니다.',
          '예약중인 설비는 활용가능으로 선택할수없습니다.'
        ),
      });
      return false;
    }
    if (
      activeAsstTpCd === 'SP' &&
      rowData.some(
        (item) => item.rvwRltDivsCd === 'PSBL' && (item.nedQty === '0' || _.isEmpty(item.nedQty))
      )
    ) {
      openMessageBar({
        type: 'error',
        content: t(
          'asst.msg.활용가능인 경우 요청수량을 입력하세요.',
          '활용가능인 경우 요청수량을 입력하세요.'
        ),
      });
      return false;
    }

    return true;
  };

  //저장 버튼
  const handleSave = () => {
    if (!validateSaveCondition('save')) return;

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        const params = (rowData || []).filter((o) => !!o.crudKey);
        saveIdleAssetReviewResult(params)
          .then((response) => {
            openMessageBar({
              type: 'confirm',
              content: t('com.msg.저장되었습니다.', '저장되었습니다.'),
            });
            // 재조회 호출
            onRefresh?.();
          })
          .catch((err) =>
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            })
          );
      },
    });
  };

  //검토완료 버튼
  const handleSaveConfirm = () => {
    if (!validateSaveCondition('confirm')) return;

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.검토완료 하시겠습니까?', '검토완료 하시겠습니까?'),
      yesCallback: () => {
        // 변경된 데이터
        const params = (rowData || []).filter((o) => !!o.crudKey);

        saveIdleAssetReviewResultEqp(rowData)
          .then((response) => {
            if (response?.successOrNot === 'Y') {
              onRefresh?.();
            }
            openMessageBar({
              type: response?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                response?.successOrNot === 'Y'
                  ? t('com.msg.검토완료 되었습니다.', '검토완료 되었습니다.')
                  : t('com.msg.오류가 발생했습니다.', '오류가 발생했습니다.'),
            });
          })
          .catch((err) =>
            openMessageBar({
              type: 'error',
              content: err,
            })
          );
      },
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>
            {activeAsstTpCd === AsstTpCd.SPARE_PARTS
              ? t('asst.label.요청 S/P 상세 내용', '요청 S/P 상세 내용')
              : activeAsstTpCd === AsstTpCd.EQUIPMENT
              ? t('asst.label.요청 설비 리스트', '요청 설비 리스트')
              : t('asst.label.요청 기타자산 리스트', '요청 기타자산 리스트')}
          </h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          &nbsp; &nbsp;
          <div style={{ fontSize: '9pt', color: '#996633' }}>
            {activeAsstTpCd === AsstTpCd.SPARE_PARTS
              ? t(
                  'asst.label.요청 S/P 상세내용',
                  ' ⓘ S/P 검토완료시 해당부서가 요청한 수량만큼 타부서의 요청가능한 잔여수량이 줄어듭니다.'
                )
              : ''}
          </div>
        </SubTitleGroup>
        <ControlBtnGroup>
          <ComboBox
            defaultValue={getCurrCdPre}
            options={code?.currCd}
            placeholder={String(t('com.label.통화단위', '통화단위'))}
            onChange={(value) => {
              setCurrChangeView(value);
            }}
            style={{ width: '80px' }}
          />

          {openMode === AsstRvwType.REPLY && (
            <>
              <ComboBox
                options={code?.rvwRltDivsCd}
                placeholder={String(t('com.label.검토결과선택', '검토결과선택'))}
                onChange={(value) => {
                  setGatingMgrTpSelected(value);
                }}
                style={{ width: '100px' }}
              />
              <CustomInputWithSearch
                name="rvwRltCtn"
                placeholder={String(t('com.label.검토결과입력', '검토결과입력'))}
                value={getRvwRltCtn} // 입력값은 변수로 변경 필요
                onChange={(e) => setRvwRltCtn(e.target.value)} // 변경 이벤트
              />
              <Button
                css={IconButton.button}
                className="setting"
                onClick={onBtContAll}
                disableRipple
              >
                {t('com.button.일괄적용', '일괄적용')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleExportExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={activeGridId}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={gridHeight}
        allowPinning={false}
        isFilter={false}
        rowSelection={openMode === AsstRvwType.READ ? 'single' : 'multiple'}
        initialized={onInitialized}
        beginningEdit={beginningEdit} //
        cellEditEnded={onCellEditEnded} // nedQty
        isSelector={true}
        autoCheck={true}
        showCheckAll={openMode !== AsstRvwType.READ}
        onChangeCheckedItem={(items) => {
          setCheckedItems(items);
        }}
      />
      {/*검토회신*/}
      {openMode === AsstRvwType.REPLY && RvwStatCd.COMPLETE !== rowData[0]?.mstRvwStatCd && (
        <GlobalBtnGroup>
          <Button
            css={IconButton.button}
            className="confirm"
            onClick={handleSaveConfirm}
            disableRipple
          >
            {t('com.button.검토완료', '검토완료')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default React.forwardRef(IdleAssetEquipmentGrid);
